import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Toolbar,
  IconButton,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  Select,
  InputLabel,
  Chip,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  GridOn as GridIcon,
  Add as AddIcon,
  FlipToFront as BringToFrontIcon,
  FlipToBack as SendToBackIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  GroupWork as GroupIcon,
  CallSplit as UngroupIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Extension as WidgetIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import pageService from './services/pageService';
import widgetService from './services/widgetService';
import widgetActionService from './services/widgetActionService';
import SimplePropertyInspector from './SimplePropertyInspector';
import { renderWidget, loadCustomWidgets as loadCustomWidgetsCache } from './widgets';
import PageIconSelector from './components/PageIconSelector';

// Property keys in widget.properties that hold tag references — used by
// "Widget Olarak Kaydet" to convert bindings into custom widget input slots.
const SINGLE_TAG_KEYS = ['tagId'];
const MULTI_TAG_KEYS = ['tagIds'];

const SimplePageDesigner = () => {
  console.log('🎨 SimplePageDesigner: Component rendering...');

  const location = useLocation();
  const [widgets, setWidgetsRaw] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const undoingRef = React.useRef(false);

  // Wrap setWidgets to track undo history
  const setWidgets = React.useCallback((updater) => {
    setWidgetsRaw(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (!undoingRef.current && JSON.stringify(prev) !== JSON.stringify(next)) {
        setUndoStack(stack => [...stack.slice(-30), prev]); // Max 30 undo
        setRedoStack([]);
      }
      return next;
    });
  }, []);

  const undo = React.useCallback(() => {
    setUndoStack(stack => {
      if (stack.length === 0) return stack;
      const newStack = [...stack];
      const previousState = newStack.pop();
      undoingRef.current = true;
      setWidgetsRaw(current => {
        setRedoStack(redo => [...redo, current]);
        return previousState;
      });
      setTimeout(() => { undoingRef.current = false; }, 0);
      return newStack;
    });
  }, []);

  const redo = React.useCallback(() => {
    setRedoStack(stack => {
      if (stack.length === 0) return stack;
      const newStack = [...stack];
      const nextState = newStack.pop();
      undoingRef.current = true;
      setWidgetsRaw(current => {
        setUndoStack(undo => [...undo, current]);
        return nextState;
      });
      setTimeout(() => { undoingRef.current = false; }, 0);
      return newStack;
    });
  }, []);

  const [containers, setContainers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set()); // Multi-select for grouping

  // "Widget Olarak Kaydet" dialog state
  const [saveAsWidgetDialog, setSaveAsWidgetDialog] = useState({
    open: false,
    widgetName: '',
    category: 'custom',
    description: '',
    isPublic: true,
    detectedInputs: [],
    sourceWidgetIds: [],
    saving: false
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [showGrid, setShowGrid] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pageName, setPageName] = useState('');
  const [pageIcon, setPageIcon] = useState('');
  const [iconSelectorOpen, setIconSelectorOpen] = useState(false);
  const [editingPageId, setEditingPageId] = useState(null);

  // Page properties
  const [pageSettingsOpen, setPageSettingsOpen] = useState(false);
  const [pageBackgroundColor, setPageBackgroundColor] = useState('#1e1e1e');
  const [pageBackgroundImage, setPageBackgroundImage] = useState('');
  const [pageBackgroundSize, setPageBackgroundSize] = useState('cover');
  const [pageBackgroundPosition, setPageBackgroundPosition] = useState('center');
  const [pageBackgroundRepeat, setPageBackgroundRepeat] = useState('no-repeat');
  const [canvasWidth, setCanvasWidth] = useState(1920);
  const [canvasHeight, setCanvasHeight] = useState(1080);

  console.log('📊 SimplePageDesigner state:', {
    widgetCount: widgets.length,
    pageName,
    editingPageId,
    selectedId
  });
  
  // Drag state for widgets
  const [dragState, setDragState] = useState({
    active: false,
    widgetId: null,
    startX: 0,
    startY: 0,
    origX: 0,
    origY: 0,
    scale: 1,
    isContainer: false,
    childOriginalPositions: {}
  });
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    widgetId: null
  });
  
  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState(null);
  
  // Keyboard shortcuts: Arrow keys, Ctrl+Z (undo), Ctrl+Y (redo)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;

      // Ctrl+Z = Undo, Ctrl+Y / Ctrl+Shift+Z = Redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }

      if (selectedId && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const delta = e.shiftKey ? 10 : 1;
        setWidgets(prev => {
          const selected = prev.find(w => w.id === selectedId);
          const groupId = selected?.groupId;
          return prev.map(w => {
            const shouldMove = w.id === selectedId || (groupId && w.groupId === groupId);
            if (!shouldMove) return w;
            let { x, y } = w;
            switch (e.key) {
              case 'ArrowUp': y = Math.max(0, y - delta); break;
              case 'ArrowDown': y = y + delta; break;
              case 'ArrowLeft': x = Math.max(0, x - delta); break;
              case 'ArrowRight': x = x + delta; break;
              default: break;
            }
            return { ...w, x, y };
          });
        });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, undo, redo]);

  // Resize state
  const [resizeState, setResizeState] = useState({
    active: false,
    widgetId: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    scale: 1
  });

  // Container resize state (for proportional child resizing)
  const [containerResizeState, setContainerResizeState] = useState({
    active: false,
    containerId: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    scale: 1,
    childWidgets: [] // Store original positions/sizes of children
  });

  // Snap lines state for alignment guides
  const [snapLines, setSnapLines] = useState({
    vertical: [],   // [{x: 100, type: 'left'}]
    horizontal: []  // [{y: 50, type: 'top'}]
  });

  // Snap threshold in pixels
  const SNAP_THRESHOLD = 8;

  // Base widget library
  const baseWidgetLibrary = useMemo(() => [
    { id: 'text-label', name: 'Text Label', icon: '🏷️', component: 'TextLabel' },
    { id: 'text-box', name: 'Text Box', icon: '📝', component: 'TextBox' },
    { id: 'button', name: 'Button', icon: '🔘', component: 'Button' },
    { id: 'button-menu', name: 'Button Menu', icon: '🎛️', component: 'ButtonMenu' },
    { id: 'set-value', name: 'Set Value', icon: '⚙️', component: 'SetValue' },
    { id: 'led-indicator', name: 'LED Indicator', icon: '💡', component: 'LEDIndicator' },
    { id: 'progress-bar', name: 'Progress Bar', icon: '📊', component: 'ProgressBar' },
    { id: 'gauge', name: 'Gauge', icon: '⏲️', component: 'Gauge' },
    { id: 'image', name: 'Image', icon: '🖼️', component: 'Image' },
    { id: 'toggle-switch', name: 'Toggle Switch', icon: '🔀', component: 'ToggleSwitch' },
    { id: 'trend-chart', name: 'Trend Chart', icon: '📈', component: 'TrendChart' },
    { id: 'combo-box', name: 'ComboBox', icon: '📋', component: 'ComboBox' },
    { id: 'box', name: 'Box', icon: '▭', component: 'Box' },
    { id: 'line', name: 'Line', icon: '➖', component: 'Line' },
    { id: 'circle', name: 'Circle', icon: '⭕', component: 'Circle' }
  ], []);

  // Custom widgets state
  const [customWidgets, setCustomWidgets] = useState([]);

  // Load custom widgets from backend
  useEffect(() => {
    console.log('🚀 SimplePageDesigner: Loading custom widgets...');
    const loadCustomWidgets = async () => {
      try {
        const widgets = await widgetService.getCustomWidgets(null, true);
        console.log('✅ Custom widgets loaded:', widgets);
        const widgetItems = (widgets || []).map(w => {
          // Handle both widgetId and id field names
          const widgetId = w.widgetId || w.WidgetId || w.id;
          console.log('🔧 Processing custom widget:', {
            widgetId: widgetId,
            widgetName: w.widgetName,
            allKeys: Object.keys(w)
          });

          let config = null;
          try {
            config = JSON.parse(w.widgetConfig);
          } catch (e) {
            console.warn('Invalid widget config:', w.widgetName);
          }

          const widgetItem = {
            id: `custom-${widgetId}`,
            name: w.widgetName,
            icon: '🔧',
            component: `custom-${widgetId}`,
            customConfig: config,
            customWidgetId: widgetId,
            isCustom: true
          };
          console.log('📦 Created widget library item:', widgetItem);
          return widgetItem;
        });
        setCustomWidgets(widgetItems);
        console.log('📋 Total custom widgets loaded:', widgetItems.length);
      } catch (error) {
        console.error('❌ Error loading custom widgets:', error);
      }
    };

    loadCustomWidgets();
    // Also populate the shared cache used by getCustomWidgetDefinition
    loadCustomWidgetsCache();
  }, []);

  // Load page data
  useEffect(() => {
    const loadPage = async () => {
      try {
        console.log('🔍 SimplePageDesigner: Loading page data...');
        const sp = new URLSearchParams(location.search || '');
        let pid = sp.get('id') || sp.get('pageId') || location.state?.pageId;
        console.log('📄 Page ID from URL/state:', pid);

        if (!pid) {
          console.log('⚠️ No pageId provided, fetching first page...');
          const list = await pageService.list();
          pid = list?.[0]?.id;
          console.log('📋 First page ID:', pid);
        }

        if (pid) {
          console.log('🔄 Fetching page data for pageId:', pid);
          const page = await pageService.get(Number(pid));
          console.log('✅ Page data received:', {
            pageName: page?.pageName,
            widgetCount: page?.widgets?.length,
            hasWidgets: Array.isArray(page?.widgets)
          });

          if (page) {
            setEditingPageId(Number(pid));
            setPageName(page.pageName || '');
            setPageIcon(page.pageIcon || '');
            const pageWidgets = Array.isArray(page.widgets) ? page.widgets : [];
            console.log('📦 Setting widgets:', pageWidgets.length, 'widgets');
            console.log('📐 Page dimensions from API:', { width: page.width, height: page.height });
            console.log('🔍 First 3 widgets:', pageWidgets.slice(0, 3).map(w => ({
              id: w.id,
              type: w.type,
              x: w.x,
              y: w.y,
              width: w.width,
              height: w.height
            })));
            setWidgets(pageWidgets);

            // Build containers array from widgets with isContainer property
            const containerWidgets = pageWidgets.filter(w => w.isContainer === true);
            if (containerWidgets.length > 0) {
              const containersFromWidgets = containerWidgets.map(w => ({
                id: w.id,
                label: w.name || w.type,
                x: w.x,
                y: w.y,
                width: w.width,
                height: w.height
              }));
              console.log('📦 Built', containersFromWidgets.length, 'containers from isContainer widgets');
              setContainers(containersFromWidgets);
            }

            // Also load containers from pageConfig if available (legacy support)
            if (page.pageConfig) {
              try {
                const cfg = JSON.parse(page.pageConfig);
                if (cfg.containers && Array.isArray(cfg.containers)) {
                  console.log('📦 Loading', cfg.containers.length, 'containers from pageConfig');
                  setContainers(prev => [...prev, ...cfg.containers]);
                }
              } catch (e) {
                console.error('Failed to parse containers:', e);
              }
            }

            // Load page properties
            if (page.width) {
              console.log('✅ Setting canvas width:', page.width);
              setCanvasWidth(page.width);
            }
            if (page.height) {
              console.log('✅ Setting canvas height:', page.height);
              setCanvasHeight(page.height);
            }
            if (page.backgroundColor) setPageBackgroundColor(page.backgroundColor);
            if (page.backgroundImage) setPageBackgroundImage(page.backgroundImage);
            if (page.backgroundSize) setPageBackgroundSize(page.backgroundSize);
            if (page.backgroundPosition) setPageBackgroundPosition(page.backgroundPosition);
            if (page.backgroundRepeat) setPageBackgroundRepeat(page.backgroundRepeat);
          } else {
            console.error('❌ Page data is null/undefined');
            toast.error('Failed to load page data');
          }
        } else {
          console.error('❌ No valid page ID found');
          toast.error('No page ID provided');
        }
      } catch (error) {
        console.error('❌ Error loading page:', error);
        toast.error(`Error loading page: ${error.message}`);
      }
    };

    loadPage();
  }, [location]);

  // No longer loading custom widgets for toolbox

  // Custom widget renderer for composite widgets with proportional scaling and action support
  const renderCustomWidget = useCallback((widget) => {
    const customComponents = widget.properties?.customComponents || widget.customComponents;
    const originalCanvasSize = widget.properties?.customCanvasSize || { width: 300, height: 200 };
    const metadata = widget.properties?.metadata || widget.metadata || {};

    console.log('Rendering custom widget:', widget.name, {
      hasComponents: !!customComponents,
      componentsLength: customComponents?.length,
      components: customComponents,
      properties: widget.properties
    });

    if (!customComponents || !Array.isArray(customComponents)) {
      console.warn('No custom components found for widget:', widget.name);
      return null;
    }

    // Calculate scale ratios for proportional resizing
    const scaleX = widget.width / originalCanvasSize.width;
    const scaleY = widget.height / originalCanvasSize.height;

    const customCanvasProps = widget.properties?.customCanvasProps || widget.customCanvasProps || {};

    // Handle custom widget action execution
    const handleComponentAction = async (componentId, actionType, eventData) => {
      try {
        const componentActions = metadata.componentActions || {};
        const actionConfig = componentActions[componentId]?.[actionType];
        
        if (!actionConfig || !actionConfig.actionBinding) {
          console.log(`No action binding found for component ${componentId}, action ${actionType}`);
          return;
        }

        // Extract referenced tags for the action
        const referencedTags = widgetActionService.extractReferencedTags(actionConfig.actionBinding);
        
        // Prepare enhanced action config
        const enhancedActionConfig = {
          ...actionConfig,
          referencedTags,
          outputTagBindings: actionConfig.outputTagBindings || {},
          outputVariableBindings: actionConfig.outputVariableBindings || {}
        };

        // Create execution context
        const context = widgetActionService.createExecutionContext(widget, metadata, eventData);

        console.log(`Executing action for component ${componentId}:`, enhancedActionConfig);
        
        // Execute the action
        const result = await widgetActionService.executeAction(enhancedActionConfig, context);
        
        if (result.success) {
          console.log(`Action executed successfully for component ${componentId}:`, result);
          toast.success(`Action executed successfully in ${result.executionTime}ms`);
        } else {
          console.error(`Action execution failed for component ${componentId}:`, result.errors);
          toast.error(`Action execution failed: ${result.errors.join(', ')}`);
        }
      } catch (error) {
        console.error(`Error handling component action for ${componentId}:`, error);
        toast.error(`Action execution error: ${error.message}`);
      }
    };

    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          backgroundColor: customCanvasProps.backgroundColor || 'transparent',
          borderWidth: customCanvasProps.borderWidth || 0,
          borderStyle: 'solid',
          borderColor: customCanvasProps.borderColor || 'transparent',
          borderRadius: customCanvasProps.borderRadius || 0,
          overflow: 'hidden'
        }}
      >
        {customComponents.map((component) => {
          // Calculate scaled positions and sizes
          const scaledX = component.x * scaleX;
          const scaledY = component.y * scaleY;
          const scaledWidth = component.width * scaleX;
          const scaledHeight = component.height * scaleY;

          console.log('Rendering component in custom widget:', {
            componentType: component.type,
            componentId: component.id,
            scaledDimensions: { width: scaledWidth, height: scaledHeight }
          });
          
          // Prepare component props with action handlers
          const componentProps = {
            ...(component.properties || component.props || {}),
            // Scale font size if it exists
            fontSize: component.properties?.fontSize || component.props?.fontSize ?
              Math.round((component.properties?.fontSize || component.props?.fontSize) * Math.min(scaleX, scaleY)) :
              undefined,
            // Scale border width proportionally
            borderWidth: component.properties?.borderWidth || component.props?.borderWidth ?
              Math.max(1, Math.round((component.properties?.borderWidth || component.props?.borderWidth) * Math.min(scaleX, scaleY))) :
              undefined,
            // Scale border radius proportionally
            borderRadius: component.properties?.borderRadius || component.props?.borderRadius ?
              Math.round((component.properties?.borderRadius || component.props?.borderRadius) * Math.min(scaleX, scaleY)) :
              undefined,
            // Ensure LED has minimum required props
            ...(component.type === 'led-indicator' && {
              state: component.properties?.state ?? 'active',
              color: component.properties?.color ?? '#4caf50',
              size: Math.max(16, Math.round((component.properties?.size || 24) * Math.min(scaleX, scaleY)))
            }),
            // Add action handlers for interactive components
            onClick: component.type === 'button' ?
              // For buttons, create wrapper that doesn't stop propagation
              (e) => {
                // Don't stop propagation for buttons, let both handlers run
                const value = e && e.target ? e.target.value : e;
                handleComponentAction(component.id, 'onClick', { type: 'click', value });
              } :
              // For other components, stop propagation
              (e) => {
                if (e && e.stopPropagation) {
                  e.stopPropagation(); // Prevent canvas selection
                }
                const value = e && e.target ? e.target.value : e;
                handleComponentAction(component.id, 'onClick', { type: 'click', value });
              },
            onChange: (e) => {
              const value = e && e.target ? e.target.value : e;
              handleComponentAction(component.id, 'onChange', { type: 'change', value });
            },
            onToggle: (value) => {
              handleComponentAction(component.id, 'onToggle', { type: 'toggle', value });
            }
          };
          
          return (
            <Box
              key={component.id}
              sx={{
                position: 'absolute',
                left: `${scaledX}px`,
                top: `${scaledY}px`,
                width: `${scaledWidth}px`,
                height: `${scaledHeight}px`,
                pointerEvents: 'auto' // Allow interaction with child components
              }}
            >
              {renderWidget({
                ...component,
                type: component.type || component.component || 'led-indicator',
                component: component.type || component.component,
                id: `${widget.id}_${component.id}`,
                width: scaledWidth,
                height: scaledHeight,
                properties: componentProps
              })}
            </Box>
          );
        })}
      </Box>
    );
  }, []);

  // Default properties for new widgets
  const getDefaultProperties = (type, customConfig = null) => {
    // If it's a custom widget, use its configuration
    if (type && type.startsWith('custom-') && customConfig) {
      // Extract components and canvas properties from custom config
      const components = customConfig.components || [];
      const canvasSize = customConfig.canvasSize || { width: 200, height: 100 };
      const canvasProperties = customConfig.canvasProperties || {};
      const metadata = customConfig.metadata || {};

      const defaults = {
        customComponents: components,
        customCanvasSize: canvasSize,
        customCanvasProps: canvasProperties,
        metadata: metadata
      };

      // Initialize input/output properties as null
      if (metadata.inputs) {
        metadata.inputs.forEach(input => {
          defaults[`input_${input.name}`] = null;
        });
      }
      if (metadata.outputs) {
        metadata.outputs.forEach(output => {
          defaults[`output_${output.name}`] = null;
        });
      }

      return defaults;
    }
    
    const commonProps = {
      // Common style properties
      backgroundColor: 'transparent',
      foreColor: '#000000',
      borderWidth: 0,
      borderColor: '#000000',
      borderRadius: 0,
      fontSize: 14,
      fontWeight: 400,
      fontFamily: 'inherit',
      opacity: 1
    };

    const typeSpecific = {
      'text-label': { text: 'Label', textAlign: 'left' },
      'text-box': { placeholder: 'Enter text...' },
      'button': { text: 'Button', variant: 'contained' },
      'button-menu': {
        options: [
          { label: 'ON', value: '1' },
          { label: 'OFF', value: '0' }
        ],
        tagId: null,
        colorMap: {},
        textColor: '#000000',
        defaultColor: '#ffffff',
        borderWidth: 2,
        borderColor: '#000000',
        borderRadius: 4
      },
      'set-value': {
        text: 'Set Value',
        tagId: null,
        placeholder: 'Enter value...',
        buttonText: 'Set',
        showButton: true,
        editable: true,
        borderWidth: 1,
        borderColor: '#000000',
        borderRadius: 4,
        textColor: '#000000'
      },
      'led-indicator': { state: false, color: '#4caf50' },
      'progress-bar': { value: 50, min: 0, max: 100 },
      'gauge': { value: 0, min: 0, max: 100, unit: '' },
      'trend-chart': { tagIds: [], maxDataPoints: 50, refreshInterval: 2000, showGrid: true, showLegend: true, dataMode: 'simulation' },
      'box': { borderWidth: 1, borderColor: '#000000' },
      'circle': { borderWidth: 1, borderColor: '#000000' },
      'line': { borderWidth: 2, borderColor: '#000000', direction: 'horizontal' }
    };

    return { ...commonProps, ...(typeSpecific[type] || {}) };
  };

  // Handle drag start from widget library
  const handleDragStart = useCallback((e, widget) => {
    setDraggedItem(widget);
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  // Handle drop on canvas
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (draggedItem) {
      console.log('🎯 Dropping widget:', {
        id: draggedItem.id,
        name: draggedItem.name,
        isCustom: draggedItem.isCustom,
        customWidgetId: draggedItem.customWidgetId,
        fullItem: draggedItem
      });

      const rect = e.currentTarget.getBoundingClientRect();
      const scale = zoom / 100;
      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;
      
      // Calculate dimensions - use custom widget canvas size if available
      let widgetWidth = 120;
      let widgetHeight = 40;
      
      if (draggedItem.isCustom && draggedItem.customConfig?.canvasSize) {
        widgetWidth = draggedItem.customConfig.canvasSize.width || 120;
        widgetHeight = draggedItem.customConfig.canvasSize.height || 60;
      } else if (draggedItem.isCustom) {
        widgetHeight = 60;
      }

      const newWidget = {
        id: `${draggedItem.id}-${Date.now()}`,
        type: draggedItem.id,
        component: draggedItem.component,
        name: draggedItem.name,
        x: Math.round(x),
        y: Math.round(y),
        width: widgetWidth,
        height: widgetHeight,
        zIndex: Math.max(...widgets.map(w => w.zIndex || 0), 0) + 1,
        properties: getDefaultProperties(draggedItem.id, draggedItem.customConfig)
      };
      
      // Add custom widget metadata if applicable
      if (draggedItem.isCustom) {
        newWidget.customWidgetId = draggedItem.customWidgetId;
        newWidget.templateName = draggedItem.name;
        console.log('📝 Added custom widget metadata:', {
          customWidgetId: newWidget.customWidgetId,
          templateName: newWidget.templateName
        });
      }

      console.log('✨ Created new widget:', newWidget);
      setWidgets(prev => [...prev, newWidget]);
      setSelectedId(newWidget.id);
      setDraggedItem(null);
    }
  }, [draggedItem, zoom]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  // Handle widget property change
  const handleWidgetChange = useCallback((updatedWidget) => {
    setWidgets(prev => prev.map(w => {
      if (w.id === updatedWidget.id) {
        // Spread properties at top level too, so renderWidget (which prioritizes
        // top-level keys like widget.text over widget.properties.text) picks up changes
        const newWidget = { ...w, ...updatedWidget, ...(updatedWidget.properties || {}) };
        // Preserve structural properties that PropertyInspector doesn't track
        if (w.groupId) newWidget.groupId = w.groupId;
        if (w.locked !== undefined) newWidget.locked = w.locked;
        return newWidget;
      }
      return w;
    }));
  }, []);

  // Handle widget deletion
  const handleWidgetDelete = useCallback((widgetId) => {
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
    setSelectedId(null);
  }, []);

  // Calculate snap lines for alignment guides
  const calculateSnapLines = useCallback((movingWidget, allWidgets) => {
    const otherWidgets = allWidgets.filter(w => w.id !== movingWidget.id);

    // Moving widget edges
    const moving = {
      left: movingWidget.x,
      right: movingWidget.x + movingWidget.width,
      top: movingWidget.y,
      bottom: movingWidget.y + movingWidget.height,
      centerX: movingWidget.x + movingWidget.width / 2,
      centerY: movingWidget.y + movingWidget.height / 2
    };

    let snapX = null, snapY = null;
    let minDiffX = SNAP_THRESHOLD, minDiffY = SNAP_THRESHOLD;
    const verticalLines = [], horizontalLines = [];

    otherWidgets.forEach(other => {
      const edges = {
        left: other.x,
        right: other.x + other.width,
        top: other.y,
        bottom: other.y + other.height,
        centerX: other.x + other.width / 2,
        centerY: other.y + other.height / 2
      };

      // Check vertical alignments (X axis)
      const xChecks = [
        { movingEdge: 'left', otherEdge: 'left' },
        { movingEdge: 'left', otherEdge: 'right' },
        { movingEdge: 'right', otherEdge: 'left' },
        { movingEdge: 'right', otherEdge: 'right' },
        { movingEdge: 'centerX', otherEdge: 'centerX' }
      ];

      xChecks.forEach(check => {
        const diff = Math.abs(moving[check.movingEdge] - edges[check.otherEdge]);
        if (diff < SNAP_THRESHOLD) {
          verticalLines.push({ x: edges[check.otherEdge], type: check.otherEdge });
          if (diff < minDiffX) {
            minDiffX = diff;
            snapX = { value: edges[check.otherEdge], edge: check.movingEdge };
          }
        }
      });

      // Check horizontal alignments (Y axis)
      const yChecks = [
        { movingEdge: 'top', otherEdge: 'top' },
        { movingEdge: 'top', otherEdge: 'bottom' },
        { movingEdge: 'bottom', otherEdge: 'top' },
        { movingEdge: 'bottom', otherEdge: 'bottom' },
        { movingEdge: 'centerY', otherEdge: 'centerY' }
      ];

      yChecks.forEach(check => {
        const diff = Math.abs(moving[check.movingEdge] - edges[check.otherEdge]);
        if (diff < SNAP_THRESHOLD) {
          horizontalLines.push({ y: edges[check.otherEdge], type: check.otherEdge });
          if (diff < minDiffY) {
            minDiffY = diff;
            snapY = { value: edges[check.otherEdge], edge: check.movingEdge };
          }
        }
      });
    });

    // Remove duplicate lines
    const uniqueVertical = verticalLines.filter((line, index, self) =>
      index === self.findIndex(l => l.x === line.x)
    );
    const uniqueHorizontal = horizontalLines.filter((line, index, self) =>
      index === self.findIndex(l => l.y === line.y)
    );

    return { snapX, snapY, verticalLines: uniqueVertical, horizontalLines: uniqueHorizontal };
  }, [SNAP_THRESHOLD]);

  // Start dragging a widget
  const handleWidgetMouseDown = useCallback((e, widget) => {
    e.stopPropagation();

    // Right-click: don't change selection, let context menu handle it
    if (e.button === 2) return;

    // Ctrl+click for multi-select (grouping)
    if (e.ctrlKey || e.metaKey) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(widget.id)) {
          next.delete(widget.id);
        } else {
          next.add(widget.id);
        }
        return next;
      });
      setSelectedId(widget.id);
      return; // Don't start drag on Ctrl+click
    }

    // Clear multi-select on normal click
    setSelectedIds(new Set());

    // If widget is locked, only select it but don't start dragging
    if (widget.locked) {
      setSelectedId(widget.id);
      return;
    }

    const scale = zoom / 100;

    // Collect sibling positions if widget is in a group
    let childOriginalPositions = {};
    if (widget.groupId) {
      widgets.forEach(w => {
        if (w.groupId === widget.groupId && w.id !== widget.id) {
          childOriginalPositions[w.id] = { x: w.x, y: w.y };
        }
      });
    }

    // If this is a container, also store child widget positions
    if (widget.isContainer === true) {
      const containerBounds = {
        x: widget.x,
        y: widget.y,
        width: widget.width,
        height: widget.height
      };

      widgets.forEach(w => {
        if (w.id === widget.id) return;
        if (
          w.x >= containerBounds.x &&
          w.y >= containerBounds.y &&
          w.x + w.width <= containerBounds.x + containerBounds.width &&
          w.y + w.height <= containerBounds.y + containerBounds.height
        ) {
          childOriginalPositions[w.id] = { x: w.x, y: w.y };
        }
      });
    }

    setDragState({
      active: true,
      widgetId: widget.id,
      startX: e.clientX,
      startY: e.clientY,
      origX: widget.x,
      origY: widget.y,
      scale,
      isContainer: widget.isContainer === true || !!widget.groupId,
      childOriginalPositions
    });
    setSelectedId(widget.id);
  }, [zoom, widgets]);

  // Global mouse handlers for dragging and resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Handle dragging
      if (dragState.active) {
        const dx = (e.clientX - dragState.startX) / dragState.scale;
        const dy = (e.clientY - dragState.startY) / dragState.scale;

        let newX = Math.max(0, dragState.origX + dx);
        let newY = Math.max(0, dragState.origY + dy);

        // Get current widget for dimensions
        const currentWidget = widgets.find(w => w.id === dragState.widgetId);
        if (!currentWidget) return;

        // Calculate snap lines for alignment
        const tempWidget = { ...currentWidget, x: newX, y: newY };
        const { snapX: alignSnapX, snapY: alignSnapY, verticalLines, horizontalLines } = calculateSnapLines(tempWidget, widgets);

        // Apply alignment snap if found
        if (alignSnapX) {
          if (alignSnapX.edge === 'left') newX = alignSnapX.value;
          else if (alignSnapX.edge === 'right') newX = alignSnapX.value - currentWidget.width;
          else if (alignSnapX.edge === 'centerX') newX = alignSnapX.value - currentWidget.width / 2;
        }
        if (alignSnapY) {
          if (alignSnapY.edge === 'top') newY = alignSnapY.value;
          else if (alignSnapY.edge === 'bottom') newY = alignSnapY.value - currentWidget.height;
          else if (alignSnapY.edge === 'centerY') newY = alignSnapY.value - currentWidget.height / 2;
        }

        // Fall back to grid snap if no alignment snap and grid enabled
        const finalX = alignSnapX ? newX : (showGrid ? Math.round(newX / 20) * 20 : newX);
        const finalY = alignSnapY ? newY : (showGrid ? Math.round(newY / 20) * 20 : newY);

        // Update snap lines for rendering
        setSnapLines({ vertical: verticalLines, horizontal: horizontalLines });

        // Calculate actual movement delta from original position
        const actualDx = finalX - dragState.origX;
        const actualDy = finalY - dragState.origY;

        setWidgets(prev => {
          if (dragState.isContainer && dragState.childOriginalPositions) {
            // Container with children - move all together
            const childIds = Object.keys(dragState.childOriginalPositions);

            return prev.map(w => {
              if (w.id === dragState.widgetId) {
                // Move the container from its original position
                return { ...w, x: finalX, y: finalY };
              } else if (childIds.includes(w.id)) {
                // Move child widget from its original position
                const origPos = dragState.childOriginalPositions[w.id];
                return { ...w, x: origPos.x + actualDx, y: origPos.y + actualDy };
              }
              return w;
            });
          } else {
            // Not a container - just move the single widget
            return prev.map(w =>
              w.id === dragState.widgetId
                ? { ...w, x: finalX, y: finalY }
                : w
            );
          }
        });

        // Also update containers state if this is a container
        if (dragState.isContainer) {
          setContainers(prev => prev.map(c => {
            if (c.id === dragState.widgetId) {
              return { ...c, x: finalX, y: finalY };
            }
            return c;
          }));
        }
      }
      
      // Handle resizing
      if (resizeState.active) {
        const dx = (e.clientX - resizeState.startX) / resizeState.scale;
        const dy = (e.clientY - resizeState.startY) / resizeState.scale;

        let newWidth = Math.max(20, resizeState.startWidth + dx);
        let newHeight = Math.max(20, resizeState.startHeight + dy);

        // Get current widget for snap calculation
        const currentWidget = widgets.find(w => w.id === resizeState.widgetId);
        if (!currentWidget) return;

        // Calculate snap for right edge (x + width) and bottom edge (y + height)
        const tempWidget = { ...currentWidget, width: newWidth, height: newHeight };
        const { snapX: alignSnapX, snapY: alignSnapY, verticalLines, horizontalLines } = calculateSnapLines(tempWidget, widgets);

        // Apply snap for resize (only right and bottom edges matter)
        if (alignSnapX && alignSnapX.edge === 'right') {
          newWidth = alignSnapX.value - currentWidget.x;
        }
        if (alignSnapY && alignSnapY.edge === 'bottom') {
          newHeight = alignSnapY.value - currentWidget.y;
        }

        // Fall back to grid snap if no alignment snap and grid enabled
        const finalWidth = (alignSnapX && alignSnapX.edge === 'right') ? newWidth : (showGrid ? Math.round(newWidth / 20) * 20 : newWidth);
        const finalHeight = (alignSnapY && alignSnapY.edge === 'bottom') ? newHeight : (showGrid ? Math.round(newHeight / 20) * 20 : newHeight);

        // Update snap lines for rendering
        setSnapLines({ vertical: verticalLines, horizontal: horizontalLines });

        setWidgets(prev => prev.map(w =>
          w.id === resizeState.widgetId
            ? { ...w, width: Math.max(20, finalWidth), height: Math.max(20, finalHeight) }
            : w
        ));
      }

      // Handle container resizing with proportional child resize
      if (containerResizeState.active) {
        const dx = (e.clientX - containerResizeState.startX) / containerResizeState.scale;
        const dy = (e.clientY - containerResizeState.startY) / containerResizeState.scale;

        let newWidth = Math.max(50, containerResizeState.startWidth + dx);
        let newHeight = Math.max(50, containerResizeState.startHeight + dy);

        // Get current container for snap calculation
        const containerWidget = widgets.find(w => w.id === containerResizeState.containerId);
        if (containerWidget) {
          // Create temp widget for snap calculation
          const tempWidget = {
            ...containerWidget,
            width: newWidth,
            height: newHeight
          };
          const { snapX: alignSnapX, snapY: alignSnapY, verticalLines, horizontalLines } = calculateSnapLines(tempWidget, widgets);

          // Apply snap for resize (only right and bottom edges matter)
          if (alignSnapX && alignSnapX.edge === 'right') {
            newWidth = alignSnapX.value - containerWidget.x;
          }
          if (alignSnapY && alignSnapY.edge === 'bottom') {
            newHeight = alignSnapY.value - containerWidget.y;
          }

          // Fall back to grid snap if no alignment snap and grid enabled
          newWidth = (alignSnapX && alignSnapX.edge === 'right') ? newWidth : (showGrid ? Math.round(newWidth / 20) * 20 : newWidth);
          newHeight = (alignSnapY && alignSnapY.edge === 'bottom') ? newHeight : (showGrid ? Math.round(newHeight / 20) * 20 : newHeight);

          // Ensure minimum size
          newWidth = Math.max(50, newWidth);
          newHeight = Math.max(50, newHeight);

          // Update snap lines for rendering
          setSnapLines({ vertical: verticalLines, horizontal: horizontalLines });
        }

        // Calculate scale factors
        const scaleX = newWidth / containerResizeState.startWidth;
        const scaleY = newHeight / containerResizeState.startHeight;

        // Update container widget
        setWidgets(prev => prev.map(w => {
          if (w.id === containerResizeState.containerId) {
            return { ...w, width: newWidth, height: newHeight };
          }
          // Update child widgets proportionally
          const childInfo = containerResizeState.childWidgets.find(c => c.id === w.id);
          if (childInfo) {
            return {
              ...w,
              x: containerResizeState.containerX + (childInfo.relX * newWidth),
              y: containerResizeState.containerY + (childInfo.relY * newHeight),
              width: Math.max(10, childInfo.relWidth * newWidth),
              height: Math.max(10, childInfo.relHeight * newHeight)
            };
          }
          return w;
        }));

        // Update containers state
        setContainers(prev => prev.map(c => {
          if (c.id === containerResizeState.containerId) {
            return { ...c, width: newWidth, height: newHeight };
          }
          return c;
        }));
      }
    };

    const handleMouseUp = () => {
      // Clear snap lines
      setSnapLines({ vertical: [], horizontal: [] });

      if (dragState.active) {
        setDragState({
          active: false,
          widgetId: null,
          startX: 0,
          startY: 0,
          origX: 0,
          origY: 0,
          scale: 1,
          isContainer: false,
          childOriginalPositions: {}
        });
      }

      if (resizeState.active) {
        setResizeState({
          active: false,
          widgetId: null,
          startX: 0,
          startY: 0,
          startWidth: 0,
          startHeight: 0,
          scale: 1
        });
      }

      if (containerResizeState.active) {
        setContainerResizeState({
          active: false,
          containerId: null,
          startX: 0,
          startY: 0,
          startWidth: 0,
          startHeight: 0,
          scale: 1,
          childWidgets: []
        });
      }
    };

    if (dragState.active || resizeState.active || containerResizeState.active) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, resizeState, containerResizeState, showGrid, widgets, calculateSnapLines]);

  // Context menu functions
  const showContextMenu = useCallback((e, widgetId = null) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      widgetId
    });
  }, []);

  const hideContextMenu = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, widgetId: null });
  }, []);

  // Z-index controls
  const bringToFront = useCallback((widgetId) => {
    const maxZ = Math.max(...widgets.map(w => w.zIndex || 0));
    setWidgets(prev => prev.map(w => 
      w.id === widgetId ? { ...w, zIndex: maxZ + 1 } : w
    ));
    hideContextMenu();
  }, [widgets, hideContextMenu]);

  const sendToBack = useCallback((widgetId) => {
    const minZ = Math.min(...widgets.map(w => w.zIndex || 0));
    setWidgets(prev => prev.map(w =>
      w.id === widgetId ? { ...w, zIndex: minZ - 1 } : w
    ));
    hideContextMenu();
  }, [widgets, hideContextMenu]);

  // Toggle lock state for a widget
  const toggleLock = useCallback((widgetId) => {
    setWidgets(prev => prev.map(w =>
      w.id === widgetId ? { ...w, locked: !w.locked } : w
    ));
    hideContextMenu();
  }, [hideContextMenu]);

  // Group/Ungroup functions
  const groupWidgets = useCallback(() => {
    if (selectedIds.size < 2) return;
    const groupId = `group-${Date.now()}`;
    setWidgets(prev => prev.map(w =>
      selectedIds.has(w.id) ? { ...w, groupId } : w
    ));
    setSelectedIds(new Set());
    hideContextMenu();
    toast.success(`${selectedIds.size} bileşen gruplandı`);
  }, [selectedIds, hideContextMenu]);

  // ===== Save selection as Custom Widget =====
  // Open the "Widget Olarak Kaydet" dialog: gather selection, detect tag bindings.
  const openSaveAsWidgetDialog = useCallback((triggerWidgetId) => {
    // Build the selection set: includes the right-clicked widget if not in selectedIds.
    let ids = new Set(selectedIds);
    if (triggerWidgetId) ids.add(triggerWidgetId);
    const triggerWidget = widgets.find(w => w.id === triggerWidgetId);
    // If trigger is part of a group, include the whole group.
    if (triggerWidget?.groupId) {
      widgets.filter(w => w.groupId === triggerWidget.groupId).forEach(w => ids.add(w.id));
    }
    const selected = widgets.filter(w => ids.has(w.id));
    if (selected.length === 0) {
      toast.error('Önce bir veya birden fazla bileşen seçin');
      hideContextMenu();
      return;
    }

    // Auto-detect tag bindings → propose inputs (I1, I2, ...). Same tag id is reused.
    const detected = [];
    const tagIdToInputName = new Map();
    const allocateInput = (tagId) => {
      if (tagId === null || tagId === undefined || tagId === '') return null;
      if (typeof tagId === 'string' && tagId.startsWith('@inputs.')) return tagId.replace('@inputs.', '');
      const key = String(tagId);
      if (tagIdToInputName.has(key)) return tagIdToInputName.get(key);
      const name = `I${detected.length + 1}`;
      tagIdToInputName.set(key, name);
      detected.push({ name, type: 'tag', defaultValue: '', tagBinding: tagId });
      return name;
    };

    selected.forEach(w => {
      const props = w.properties || {};
      SINGLE_TAG_KEYS.forEach(k => {
        if (props[k] !== undefined && props[k] !== null && props[k] !== '') {
          allocateInput(props[k]);
        }
      });
      MULTI_TAG_KEYS.forEach(k => {
        if (Array.isArray(props[k])) props[k].forEach(allocateInput);
      });
    });

    setSaveAsWidgetDialog({
      open: true,
      widgetName: '',
      category: 'custom',
      description: '',
      isPublic: true,
      detectedInputs: detected,
      sourceWidgetIds: [...ids],
      saving: false
    });
    hideContextMenu();
  }, [widgets, selectedIds, hideContextMenu]);

  // Actually persist the selection as a Custom Widget.
  const confirmSaveAsWidget = useCallback(async () => {
    const { widgetName, category, description, isPublic, detectedInputs, sourceWidgetIds } = saveAsWidgetDialog;
    if (!widgetName.trim()) {
      toast.error('Widget adı zorunlu');
      return;
    }

    const selected = widgets.filter(w => sourceWidgetIds.includes(w.id));
    if (selected.length === 0) {
      toast.error('Kaynak bileşen kalmadı');
      setSaveAsWidgetDialog(s => ({ ...s, open: false }));
      return;
    }

    // Bounding box → relative coords for the new widget canvas.
    const minX = Math.min(...selected.map(w => w.x));
    const minY = Math.min(...selected.map(w => w.y));
    const maxX = Math.max(...selected.map(w => w.x + (w.width || 0)));
    const maxY = Math.max(...selected.map(w => w.y + (w.height || 0)));
    const canvasSize = { width: Math.ceil(maxX - minX), height: Math.ceil(maxY - minY) };

    // tagId → input-name lookup for replacement.
    const tagToInput = new Map();
    detectedInputs.forEach(inp => {
      if (inp.tagBinding !== undefined && inp.tagBinding !== null && inp.tagBinding !== '') {
        tagToInput.set(String(inp.tagBinding), inp.name);
      }
    });

    // Transform selected widgets → component records with relative position and @inputs. bindings.
    const components = selected.map(w => {
      const newProps = { ...(w.properties || {}) };
      SINGLE_TAG_KEYS.forEach(k => {
        const v = newProps[k];
        if (v !== undefined && v !== null && v !== '' && tagToInput.has(String(v))) {
          newProps[k] = `@inputs.${tagToInput.get(String(v))}`;
        }
      });
      MULTI_TAG_KEYS.forEach(k => {
        if (Array.isArray(newProps[k])) {
          newProps[k] = newProps[k].map(v =>
            v !== undefined && v !== null && v !== '' && tagToInput.has(String(v))
              ? `@inputs.${tagToInput.get(String(v))}`
              : v
          );
        }
      });
      return {
        id: w.id,
        type: w.type,
        x: w.x - minX,
        y: w.y - minY,
        width: w.width,
        height: w.height,
        zIndex: w.zIndex,
        properties: newProps
      };
    });

    const widgetConfig = {
      components,
      canvasSize,
      canvasProperties: {
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderColor: '#dddddd',
        borderRadius: 0,
        showGrid: false,
        gridSize: 20,
        gridColor: '#cccccc'
      },
      metadata: {
        inputs: detectedInputs,
        outputs: [],
        parameters: [],
        componentActions: {}
      },
      containers: []
    };

    try {
      setSaveAsWidgetDialog(s => ({ ...s, saving: true }));
      await widgetService.createCustomWidget({
        widgetName: widgetName.trim(),
        widgetType: 'custom',
        category,
        description,
        isPublic,
        widgetConfig: JSON.stringify(widgetConfig)
      });
      toast.success(`Widget '${widgetName}' kütüphaneye kaydedildi (${components.length} bileşen, ${detectedInputs.length} input)`);
      // Refresh custom widget list for the library panel (same shape as initial loader).
      try {
        const refreshed = await widgetService.getCustomWidgets(null, true);
        const refreshedItems = (refreshed || []).map(w => {
          const wid = w.widgetId || w.WidgetId || w.id;
          let cfg = null;
          try { cfg = JSON.parse(w.widgetConfig); } catch (e) {}
          return {
            id: `custom-${wid}`,
            name: w.widgetName,
            icon: '🔧',
            component: `custom-${wid}`,
            customConfig: cfg,
            customWidgetId: wid,
            isCustom: true
          };
        });
        setCustomWidgets(refreshedItems);
        loadCustomWidgetsCache();
      } catch (e) {
        console.warn('Widget kütüphanesi yenilenirken hata:', e);
      }
      setSaveAsWidgetDialog({
        open: false, widgetName: '', category: 'custom', description: '',
        isPublic: true, detectedInputs: [], sourceWidgetIds: [], saving: false
      });
    } catch (err) {
      console.error(err);
      toast.error('Widget kaydedilemedi: ' + (err?.message || ''));
      setSaveAsWidgetDialog(s => ({ ...s, saving: false }));
    }
  }, [saveAsWidgetDialog, widgets]);

  const ungroupWidget = useCallback((widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget?.groupId) return;
    const gid = widget.groupId;
    setWidgets(prev => prev.map(w =>
      w.groupId === gid ? { ...w, groupId: undefined } : w
    ));
    setSelectedIds(new Set());
    hideContextMenu();
    toast.success('Grup çözüldü');
  }, [widgets, hideContextMenu]);

  // Copy/Paste functions (group-aware)
  const copyWidget = useCallback((widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget) { hideContextMenu(); return; }

    if (widget.groupId) {
      // Copy entire group
      const groupWidgets = widgets.filter(w => w.groupId === widget.groupId);
      setClipboard(JSON.parse(JSON.stringify(groupWidgets)));
    } else {
      setClipboard(JSON.parse(JSON.stringify([widget])));
    }
    hideContextMenu();
  }, [widgets, hideContextMenu]);

  const pasteWidget = useCallback((x = null, y = null) => {
    if (!clipboard || !Array.isArray(clipboard) || clipboard.length === 0) { hideContextMenu(); return; }

    const maxZ = Math.max(...widgets.map(w => w.zIndex || 0), 0);
    const newGroupId = clipboard.length > 1 ? `group-${Date.now()}` : undefined;

    // Calculate offset from first widget
    const baseX = clipboard[0].x;
    const baseY = clipboard[0].y;
    const offsetX = (x !== null ? x : baseX + 20) - baseX;
    const offsetY = (y !== null ? y : baseY + 20) - baseY;

    const newWidgets = clipboard.map((w, i) => ({
      ...w,
      id: `${w.type}-${Date.now()}-${i}`,
      x: w.x + offsetX,
      y: w.y + offsetY,
      zIndex: maxZ + 1 + i,
      groupId: newGroupId
    }));

    setWidgets(prev => [...prev, ...newWidgets]);
    setSelectedId(newWidgets[0].id);
    hideContextMenu();
  }, [clipboard, widgets, hideContextMenu]);

  const deleteWidget = useCallback((widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (widget?.groupId) {
      // Delete entire group
      const gid = widget.groupId;
      setWidgets(prev => prev.filter(w => w.groupId !== gid));
    } else {
      setWidgets(prev => prev.filter(w => w.id !== widgetId));
    }
    setSelectedId(null);
    hideContextMenu();
  }, [widgets, hideContextMenu]);

  // Resize functionality
  const handleResizeStart = useCallback((e, widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget) return;

    setResizeState({
      active: true,
      widgetId,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: widget.width,
      startHeight: widget.height,
      scale: zoom / 100
    });
  }, [widgets, zoom]);

  // Container resize functionality - resizes children proportionally
  const handleContainerResizeStart = useCallback((e, containerId, container) => {
    const containerWidget = widgets.find(w => w.id === containerId);
    if (!containerWidget) return;

    // Find all widgets inside this container
    const containerBounds = {
      x: container.x,
      y: container.y,
      width: container.width,
      height: container.height
    };

    const childWidgets = widgets.filter(w => {
      if (w.id === containerId) return false;
      return (
        w.x >= containerBounds.x &&
        w.y >= containerBounds.y &&
        w.x + w.width <= containerBounds.x + containerBounds.width &&
        w.y + w.height <= containerBounds.y + containerBounds.height
      );
    }).map(w => ({
      id: w.id,
      originalX: w.x,
      originalY: w.y,
      originalWidth: w.width,
      originalHeight: w.height,
      // Relative position within container (0-1)
      relX: (w.x - containerBounds.x) / containerBounds.width,
      relY: (w.y - containerBounds.y) / containerBounds.height,
      relWidth: w.width / containerBounds.width,
      relHeight: w.height / containerBounds.height
    }));

    setContainerResizeState({
      active: true,
      containerId,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: container.width,
      startHeight: container.height,
      containerX: container.x,
      containerY: container.y,
      scale: zoom / 100,
      childWidgets
    });
  }, [widgets, zoom]);

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleGlobalClick = () => {
      if (contextMenu.visible) {
        hideContextMenu();
      }
    };
    
    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [contextMenu.visible, hideContextMenu]);

  // Save page
  const handleSave = useCallback(() => {
    setSaveDialogOpen(true);
  }, []);

  const handleSaveConfirm = useCallback(async () => {
    if (!pageName.trim()) return;

    try {
      console.log('💾 Saving page with widgets:', widgets.slice(0, 3).map(w => ({
        id: w.id,
        type: w.type,
        x: w.x,
        y: w.y,
        width: w.width,
        height: w.height
      })));

      const payload = {
        pageName: pageName.trim(),
        pageIcon: pageIcon,
        description: '',
        isActive: true,
        widgets,
        width: canvasWidth,
        height: canvasHeight,
        backgroundColor: pageBackgroundColor,
        backgroundImage: pageBackgroundImage,
        backgroundSize: pageBackgroundSize,
        backgroundPosition: pageBackgroundPosition,
        backgroundRepeat: pageBackgroundRepeat
      };

      if (editingPageId) {
        await pageService.update(editingPageId, payload);
      } else {
        await pageService.create(payload);
      }

      toast.success('Page saved successfully');
      setSaveDialogOpen(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Error saving page');
    }
  }, [pageName, pageIcon, widgets, editingPageId, canvasWidth, canvasHeight, pageBackgroundColor, pageBackgroundImage, pageBackgroundSize, pageBackgroundPosition, pageBackgroundRepeat]);

  const selectedWidget = widgets.find(w => w.id === selectedId);

  // Helper function to check if a widget is inside a container
  const isWidgetInContainer = useCallback((widget, container) => {
    return (
      widget.x >= container.x &&
      widget.y >= container.y &&
      widget.x + widget.width <= container.x + container.width &&
      widget.y + widget.height <= container.y + container.height
    );
  }, []);

  // Helper function to find which container a widget belongs to
  const getWidgetContainer = useCallback((widget) => {
    return containers.find(c => isWidgetInContainer(widget, c));
  }, [containers, isWidgetInContainer]);

  // Group widgets by container
  const widgetsByContainer = useCallback(() => {
    const groups = {};
    const orphans = [];
    const containerIds = new Set(containers.map(c => c.id));

    widgets.forEach(widget => {
      // Skip if this widget IS a container (don't render it as child)
      if (containerIds.has(widget.id)) {
        return;
      }

      const container = getWidgetContainer(widget);
      if (container) {
        if (!groups[container.id]) {
          groups[container.id] = [];
        }
        groups[container.id].push(widget);
      } else {
        orphans.push(widget);
      }
    });

    return { groups, orphans };
  }, [widgets, getWidgetContainer, containers]);

  // Safe widget rendering with error handling
  const renderWidgetSafely = useCallback((widget) => {
    try {
      const rendered = widget.type && widget.type.startsWith('custom-') ?
        renderCustomWidget(widget) :
        renderWidget(widget);

      return rendered || (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          bgcolor: 'background.paper',
          color: 'text.secondary'
        }}>
          <Typography variant="caption">
            {widget.name || widget.type}
          </Typography>
        </Box>
      );
    } catch (error) {
      console.error('❌ Failed to render widget:', widget.id, widget.type, error);
      return (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          bgcolor: '#ffebee',
          color: '#c62828',
          padding: 1
        }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            ⚠️ Render Error
          </Typography>
          <Typography variant="caption">
            {widget.type || 'unknown'}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '10px' }}>
            ID: {widget.id}
          </Typography>
        </Box>
      );
    }
  }, [renderCustomWidget]);

  console.log('🖼️ About to render SimplePageDesigner with', widgets.length, 'widgets');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <Paper elevation={1}>
        <Toolbar variant="dense">
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Simple SCADA Designer
          </Typography>
          <IconButton onClick={handleSave} color="primary">
            <SaveIcon />
          </IconButton>
          <IconButton onClick={() => setPageSettingsOpen(true)} color="default" title="Page Settings">
            <SettingsIcon />
          </IconButton>
          <IconButton onClick={undo} disabled={undoStack.length === 0} title="Geri Al (Ctrl+Z)">
            <UndoIcon />
          </IconButton>
          <IconButton onClick={redo} disabled={redoStack.length === 0} title="Yinele (Ctrl+Y)">
            <RedoIcon />
          </IconButton>
          <IconButton onClick={() => setZoom(prev => Math.max(prev - 10, 50))}>
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="body2" sx={{ mx: 1 }}>
            {zoom}%
          </Typography>
          <IconButton onClick={() => setZoom(prev => Math.min(prev + 10, 200))}>
            <ZoomInIcon />
          </IconButton>
          <IconButton 
            onClick={() => setShowGrid(!showGrid)}
            color={showGrid ? 'primary' : 'default'}
          >
            <GridIcon />
          </IconButton>
        </Toolbar>
      </Paper>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Widget Library */}
        <Paper sx={{ width: 250, overflow: 'auto', borderRight: 1, borderColor: 'divider' }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Controls
            </Typography>
            {/* All Widgets in single section */}
            <Grid container spacing={1}>
              {baseWidgetLibrary.map((widget) => (
                <Grid item xs={6} key={widget.id}>
                  <Card 
                    sx={{ 
                      cursor: 'grab',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, widget)}
                    title={`Add ${widget.name} to page`}
                  >
                    <CardContent sx={{ p: 1, textAlign: 'center', '&:last-child': { pb: 1 } }}>
                      <Typography variant="h5" sx={{ mb: 0.5 }}>
                        {widget.icon}
                      </Typography>
                      <Typography variant="caption">
                        {widget.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Custom Widgets Section */}
            {customWidgets.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Custom Widgets
                </Typography>
                <Grid container spacing={1}>
                  {customWidgets.map((widget) => (
                    <Grid item xs={6} key={widget.id}>
                      <Card
                        sx={{
                          cursor: 'grab',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, widget)}
                        title={`Add ${widget.name} to page`}
                      >
                        <CardContent sx={{ p: 1, textAlign: 'center', '&:last-child': { pb: 1 } }}>
                          <Typography variant="h5" sx={{ mb: 0.5 }}>
                            {widget.icon}
                          </Typography>
                          <Typography variant="caption">
                            {widget.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>
        </Paper>

        {/* Canvas */}
        <Box sx={{
          flex: 1,
          position: 'relative',
          overflow: 'auto',
          padding: 0,
          margin: 0,
          bgcolor: pageBackgroundColor
        }}>
          {/* Wrapper to contain scaled canvas */}
          <Box sx={{
            width: zoom !== 100 ? `${canvasWidth * (zoom / 100)}px` : canvasWidth,
            height: zoom !== 100 ? `${canvasHeight * (zoom / 100)}px` : canvasHeight,
            position: 'relative',
            margin: 0,
            padding: 0,
            overflow: 'hidden'
          }}>
            <Box
              data-canvas="true"
              sx={{
                width: canvasWidth,
                height: canvasHeight,
                position: 'relative',
                margin: 0,
                padding: 0,
                backgroundColor: pageBackgroundColor,
                backgroundImage: pageBackgroundImage ?
                  `url(${pageBackgroundImage})${showGrid ? ', radial-gradient(circle, #ccc 1px, transparent 1px)' : ''}` :
                  (showGrid ? 'radial-gradient(circle, #ccc 1px, transparent 1px)' : 'none'),
                backgroundSize: pageBackgroundImage ?
                  `${pageBackgroundSize}${showGrid ? ', 20px 20px' : ''}` :
                  (showGrid ? '20px 20px' : 'none'),
                backgroundPosition: pageBackgroundImage ? pageBackgroundPosition : 'initial',
                backgroundRepeat: pageBackgroundImage ? pageBackgroundRepeat : 'initial',
                transform: zoom !== 100 ? `scale(${zoom / 100})` : 'none',
                transformOrigin: 'top left',
                boxSizing: 'border-box',
                '& *': {
                  boxSizing: 'border-box'
                }
              }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => { setSelectedId(null); setSelectedIds(new Set()); }}
            onContextMenu={(e) => showContextMenu(e)}
          >
            {/* Render widgets with container hierarchy */}
            {(() => {
              if (containers.length === 0) {
                // No containers - render all widgets normally
                return widgets
                  .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                  .map((widget) => (
                    <Box
                      key={widget.id}
                      onMouseDown={(e) => handleWidgetMouseDown(e, widget)}
                      onContextMenu={(e) => showContextMenu(e, widget.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(widget.id);
                      }}
                      sx={{
                        position: 'absolute',
                        left: widget.x,
                        top: widget.y,
                        width: widget.width,
                        height: widget.height,
                        zIndex: widget.zIndex || 0,
                        border: selectedId === widget.id ? '2px solid #1976d2' : selectedIds.has(widget.id) ? '2px dashed #ff9800' : widget.groupId ? '1px dashed #4caf5088' : 'none',
                        borderRadius: 0,
                        cursor: widget.locked ? 'default' : (dragState.active && dragState.widgetId === widget.id ? 'grabbing' : 'grab'),
                        overflow: 'visible',
                        userSelect: 'none',
                        boxSizing: 'border-box',
                        '&:hover': {
                          boxShadow: selectedId === widget.id ? '0 0 0 2px #1976d2' : '0 0 0 1px #999'
                        }
                      }}
                    >
                      {renderWidgetSafely(widget)}
                      {/* Lock indicator */}
                      {widget.locked && (
                        <Box sx={{ position: 'absolute', top: 2, left: 2, zIndex: 1001, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '4px', p: 0.25 }}>
                          <LockIcon sx={{ fontSize: 14, color: 'white' }} />
                        </Box>
                      )}
                      {selectedId === widget.id && !widget.locked && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: -6,
                            bottom: -6,
                            width: 12,
                            height: 12,
                            backgroundColor: '#1976d2',
                            borderRadius: '2px',
                            cursor: 'se-resize',
                            zIndex: 1000,
                            border: '1px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            handleResizeStart(e, widget.id);
                          }}
                        />
                      )}
                    </Box>
                  ));
              }

              // With containers - create proper container hierarchy
              const { groups, orphans } = widgetsByContainer();

              return (
                <>
                  {/* Render containers with their widgets */}
                  {containers.map((container, containerIndex) => {
                    // Find the actual container widget (frame/box)
                    const containerWidget = widgets.find(w => w.id === container.id);
                    if (!containerWidget) return null;

                    const containerWidgets = (groups[container.id] || [])
                      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

                    return (
                      <Box
                        key={container.id}
                        data-container-id={container.id}
                        data-container-label={container.label}
                        sx={{
                          position: 'absolute',
                          left: container.x,
                          top: container.y,
                          width: container.width,
                          height: container.height,
                          zIndex: containerWidget.zIndex || 0,
                          isolation: 'isolate'
                        }}
                      >
                        {/* Render the container itself (frame/box) */}
                        <Box
                          onMouseDown={(e) => handleWidgetMouseDown(e, containerWidget)}
                          onContextMenu={(e) => showContextMenu(e, containerWidget.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedId(containerWidget.id);
                          }}
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            border: selectedId === containerWidget.id ? '2px solid #ffeb3b' : 'none',
                            cursor: containerWidget.locked ? 'default' : (dragState.active && dragState.widgetId === containerWidget.id ? 'grabbing' : 'grab'),
                            userSelect: 'none',
                            boxSizing: 'border-box',
                            '&:hover': {
                              boxShadow: selectedId === containerWidget.id ? '0 0 0 2px #ffeb3b' : '0 0 0 1px #999'
                            }
                          }}
                        >
                          {renderWidgetSafely(containerWidget)}
                          {/* Lock indicator for container */}
                          {containerWidget.locked && (
                            <Box sx={{ position: 'absolute', top: 2, left: 2, zIndex: 1001, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '4px', p: 0.25 }}>
                              <LockIcon sx={{ fontSize: 14, color: 'white' }} />
                            </Box>
                          )}
                          {/* Resize handle for container */}
                          {selectedId === containerWidget.id && !containerWidget.locked && (
                            <Box
                              sx={{
                                position: 'absolute',
                                right: -6,
                                bottom: -6,
                                width: 12,
                                height: 12,
                                backgroundColor: '#ffeb3b',
                                borderRadius: '2px',
                                cursor: 'se-resize',
                                zIndex: 1000,
                                border: '1px solid white',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                handleContainerResizeStart(e, containerWidget.id, container);
                              }}
                            />
                          )}
                        </Box>

                        {/* Render widgets inside container */}
                        {containerWidgets.map((widget) => (
                          <Box
                            key={widget.id}
                            onMouseDown={(e) => handleWidgetMouseDown(e, widget)}
                            onContextMenu={(e) => showContextMenu(e, widget.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(widget.id);
                            }}
                            sx={{
                              position: 'absolute',
                              left: widget.x - container.x,
                              top: widget.y - container.y,
                              width: widget.width,
                              height: widget.height,
                              zIndex: widget.zIndex || 0,
                              border: selectedId === widget.id ? '2px solid #1976d2' : selectedIds.has(widget.id) ? '2px dashed #ff9800' : widget.groupId ? '1px dashed #4caf5088' : 'none',
                              borderRadius: 0,
                              cursor: widget.locked ? 'default' : (dragState.active && dragState.widgetId === widget.id ? 'grabbing' : 'grab'),
                              overflow: 'visible',
                              userSelect: 'none',
                              boxSizing: 'border-box',
                              pointerEvents: 'auto', // Enable interaction
                              '&:hover': {
                                boxShadow: selectedId === widget.id ? '0 0 0 2px #1976d2' : '0 0 0 1px #999'
                              }
                            }}
                          >
                            {renderWidgetSafely(widget)}
                            {/* Lock indicator */}
                            {widget.locked && (
                              <Box sx={{ position: 'absolute', top: 2, left: 2, zIndex: 1001, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '4px', p: 0.25 }}>
                                <LockIcon sx={{ fontSize: 14, color: 'white' }} />
                              </Box>
                            )}
                            {selectedId === widget.id && !widget.locked && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: -8,
                                  bottom: -8,
                                  width: 16,
                                  height: 16,
                                  backgroundColor: '#1976d2',
                                  borderRadius: '3px',
                                  cursor: 'se-resize',
                                  zIndex: 1000,
                                  border: '2px solid white',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  handleResizeStart(e, widget.id);
                                }}
                              />
                            )}
                          </Box>
                        ))}
                      </Box>
                    );
                  })}

                  {/* Render orphan widgets */}
                  {orphans
                    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                    .map((widget) => (
                      <Box
                        key={widget.id}
                        onMouseDown={(e) => handleWidgetMouseDown(e, widget)}
                        onContextMenu={(e) => showContextMenu(e, widget.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(widget.id);
                        }}
                        sx={{
                          position: 'absolute',
                          left: widget.x,
                          top: widget.y,
                          width: widget.width,
                          height: widget.height,
                          zIndex: widget.zIndex || 0,
                          border: selectedId === widget.id ? '2px solid #1976d2' : selectedIds.has(widget.id) ? '2px dashed #ff9800' : widget.groupId ? '1px dashed #4caf5088' : 'none',
                          borderRadius: 0,
                          cursor: widget.locked ? 'default' : (dragState.active && dragState.widgetId === widget.id ? 'grabbing' : 'grab'),
                          overflow: 'visible',
                          userSelect: 'none',
                          boxSizing: 'border-box',
                          '&:hover': {
                            boxShadow: selectedId === widget.id ? '0 0 0 2px #1976d2' : '0 0 0 1px #999'
                          }
                        }}
                      >
                        {renderWidgetSafely(widget)}
                        {/* Lock indicator */}
                        {widget.locked && (
                          <Box sx={{ position: 'absolute', top: 2, left: 2, zIndex: 1001, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '4px', p: 0.25 }}>
                            <LockIcon sx={{ fontSize: 14, color: 'white' }} />
                          </Box>
                        )}
                        {selectedId === widget.id && !widget.locked && (
                          <Box
                            sx={{
                              position: 'absolute',
                              right: -8,
                              bottom: -8,
                              width: 16,
                              height: 16,
                              backgroundColor: '#1976d2',
                              borderRadius: '3px',
                              cursor: 'se-resize',
                              zIndex: 1000,
                              border: '2px solid white',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handleResizeStart(e, widget.id);
                            }}
                          />
                        )}
                      </Box>
                    ))}
                </>
              );
            })()}

            {/* Drop zone indicator */}
            {widgets.length === 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: 'text.secondary'
                }}
              >
                <AddIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6">
                  Drag widgets here to start designing
                </Typography>
              </Box>
            )}

            {/* Snap Guide Lines */}
            {snapLines.vertical.map((line, i) => {
              // Color based on alignment type
              const color = line.type === 'left' ? '#4caf50' :      // Green for left edge
                           line.type === 'right' ? '#2196f3' :       // Blue for right edge
                           line.type === 'centerX' ? '#9c27b0' :     // Purple for center
                           '#2196f3';
              return (
                <Box
                  key={`snap-v-${i}`}
                  sx={{
                    position: 'absolute',
                    left: line.x,
                    top: 0,
                    width: line.type === 'centerX' ? '2px' : '1px',
                    height: '100%',
                    backgroundColor: color,
                    pointerEvents: 'none',
                    zIndex: 10000,
                    opacity: 0.8
                  }}
                />
              );
            })}
            {snapLines.horizontal.map((line, i) => {
              // Color based on alignment type
              const color = line.type === 'top' ? '#ff9800' :        // Orange for top edge
                           line.type === 'bottom' ? '#f44336' :       // Red for bottom edge
                           line.type === 'centerY' ? '#9c27b0' :      // Purple for center
                           '#2196f3';
              return (
                <Box
                  key={`snap-h-${i}`}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: line.y,
                    width: '100%',
                    height: line.type === 'centerY' ? '2px' : '1px',
                    backgroundColor: color,
                    pointerEvents: 'none',
                    zIndex: 10000,
                    opacity: 0.8
                  }}
                />
              );
            })}
          </Box>
          </Box>
        </Box>

        {/* Property Inspector */}
        <Box sx={{ width: 320, borderLeft: 1, borderColor: 'divider' }}>
          <SimplePropertyInspector
            widget={selectedWidget}
            onChange={handleWidgetChange}
            onDelete={handleWidgetDelete}
            onToggleLock={toggleLock}
            customWidgetDefs={customWidgets}
          />
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu.visible}
        onClose={hideContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu.visible
            ? { top: contextMenu.y, left: contextMenu.x }
            : undefined
        }
      >
        {contextMenu.widgetId ? (
          // Widget context menu
          <>
            <MenuItem onClick={() => bringToFront(contextMenu.widgetId)}>
              <ListItemIcon>
                <BringToFrontIcon />
              </ListItemIcon>
              <ListItemText>Bring to Front</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => sendToBack(contextMenu.widgetId)}>
              <ListItemIcon>
                <SendToBackIcon />
              </ListItemIcon>
              <ListItemText>Send to Back</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => toggleLock(contextMenu.widgetId)}>
              <ListItemIcon>
                {widgets.find(w => w.id === contextMenu.widgetId)?.locked ? <LockOpenIcon /> : <LockIcon />}
              </ListItemIcon>
              <ListItemText>
                {widgets.find(w => w.id === contextMenu.widgetId)?.locked ? 'Unlock' : 'Lock'}
              </ListItemText>
            </MenuItem>

            {/* Group/Ungroup */}
            {selectedIds.size >= 2 && (
              <MenuItem onClick={groupWidgets}>
                <ListItemIcon><GroupIcon /></ListItemIcon>
                <ListItemText>Grupla ({selectedIds.size})</ListItemText>
              </MenuItem>
            )}

            {/* Widget Olarak Kaydet — current widget or whole selection (>=1) */}
            <MenuItem onClick={() => openSaveAsWidgetDialog(contextMenu.widgetId)}>
              <ListItemIcon><WidgetIcon /></ListItemIcon>
              <ListItemText>
                Widget Olarak Kaydet{selectedIds.size >= 2 ? ` (${selectedIds.size})` : ''}
              </ListItemText>
            </MenuItem>
            {widgets.find(w => w.id === contextMenu.widgetId)?.groupId && (
              <MenuItem onClick={() => ungroupWidget(contextMenu.widgetId)}>
                <ListItemIcon><UngroupIcon /></ListItemIcon>
                <ListItemText>Grubu Çöz</ListItemText>
              </MenuItem>
            )}

            <Divider />

            <MenuItem onClick={() => copyWidget(contextMenu.widgetId)}>
              <ListItemIcon>
                <CopyIcon />
              </ListItemIcon>
              <ListItemText>Copy</ListItemText>
            </MenuItem>
            
            <MenuItem 
              onClick={pasteWidget}
              disabled={!clipboard}
            >
              <ListItemIcon>
                <PasteIcon />
              </ListItemIcon>
              <ListItemText>Paste</ListItemText>
            </MenuItem>
            
            <Divider />
            
            <MenuItem onClick={() => deleteWidget(contextMenu.widgetId)}>
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        ) : (
          // Canvas context menu
          <MenuItem 
            onClick={() => {
              const canvasRect = document.querySelector('[data-canvas]')?.getBoundingClientRect();
              if (canvasRect) {
                const x = (contextMenu.x - canvasRect.left) / (zoom / 100);
                const y = (contextMenu.y - canvasRect.top) / (zoom / 100);
                pasteWidget(Math.max(0, x), Math.max(0, y));
              } else {
                pasteWidget();
              }
            }}
            disabled={!clipboard}
          >
            <ListItemIcon>
              <PasteIcon />
            </ListItemIcon>
            <ListItemText>Paste</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Save Selection As Widget Dialog */}
      <Dialog
        open={saveAsWidgetDialog.open}
        onClose={() => !saveAsWidgetDialog.saving && setSaveAsWidgetDialog(s => ({ ...s, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WidgetIcon fontSize="small" />
            Seçimi Widget Olarak Kaydet
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Widget Adı"
              value={saveAsWidgetDialog.widgetName}
              onChange={(e) => setSaveAsWidgetDialog(s => ({ ...s, widgetName: e.target.value }))}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Kategori</InputLabel>
              <Select
                label="Kategori"
                value={saveAsWidgetDialog.category}
                onChange={(e) => setSaveAsWidgetDialog(s => ({ ...s, category: e.target.value }))}
              >
                {widgetService.getWidgetCategories().map(c => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Açıklama (opsiyonel)"
              value={saveAsWidgetDialog.description}
              onChange={(e) => setSaveAsWidgetDialog(s => ({ ...s, description: e.target.value }))}
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveAsWidgetDialog.isPublic}
                  onChange={(e) => setSaveAsWidgetDialog(s => ({ ...s, isPublic: e.target.checked }))}
                />
              }
              label="Tüm şirketlerle paylaş (Public)"
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Seçim: {saveAsWidgetDialog.sourceWidgetIds.length} bileşen
              </Typography>
              {saveAsWidgetDialog.detectedInputs.length > 0 ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Tespit edilen tag bağlantıları input olarak kaydedilecek. Widget yeniden eklenirken bu input'lar yeniden bağlanabilir.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {saveAsWidgetDialog.detectedInputs.map(inp => (
                      <Chip
                        key={inp.name}
                        size="small"
                        label={`${inp.name} ← tag #${inp.tagBinding}`}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Alert>
              ) : (
                <Alert severity="info">Tag bağlantısı tespit edilmedi — statik bir widget olarak kaydedilecek.</Alert>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSaveAsWidgetDialog(s => ({ ...s, open: false }))}
            disabled={saveAsWidgetDialog.saving}
          >
            Vazgeç
          </Button>
          <Button
            variant="contained"
            onClick={confirmSaveAsWidget}
            disabled={saveAsWidgetDialog.saving || !saveAsWidgetDialog.widgetName.trim()}
          >
            {saveAsWidgetDialog.saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save SCADA Page</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Page Name"
            value={pageName}
            onChange={(e) => setPageName(e.target.value)}
            margin="dense"
            variant="outlined"
          />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">Sayfa İkonu:</Typography>
            <Box
              onClick={() => setIconSelectorOpen(true)}
              sx={{
                width: 64,
                height: 64,
                border: '2px dashed #ccc',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#e3f2fd'
                }
              }}
            >
              {pageIcon ? (
                <img src={pageIcon} alt="Page Icon" style={{ width: 48, height: 48, objectFit: 'contain' }} />
              ) : (
                <Typography variant="caption" color="text.secondary">Seç</Typography>
              )}
            </Box>
            {pageIcon && (
              <Button size="small" color="secondary" onClick={() => setPageIcon('')}>
                Temizle
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveConfirm}
            variant="contained"
            disabled={!pageName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Page Icon Selector Dialog */}
      <PageIconSelector
        open={iconSelectorOpen}
        onClose={() => setIconSelectorOpen(false)}
        onSelect={(icon) => setPageIcon(icon)}
        currentIcon={pageIcon}
      />

      {/* Page Settings Dialog */}
      <Dialog open={pageSettingsOpen} onClose={() => setPageSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Page Settings</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>
            Background
          </Typography>
          <TextField
            fullWidth
            label="Background Color"
            type="color"
            value={pageBackgroundColor}
            onChange={(e) => setPageBackgroundColor(e.target.value)}
            margin="dense"
            variant="outlined"
            helperText="Choose canvas background color"
          />
          <TextField
            fullWidth
            label="Background Image URL"
            value={pageBackgroundImage}
            onChange={(e) => setPageBackgroundImage(e.target.value)}
            margin="dense"
            variant="outlined"
            helperText="Enter image URL (e.g., https://example.com/image.jpg)"
            placeholder="https://example.com/image.jpg"
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Background Size"
                value={pageBackgroundSize}
                onChange={(e) => setPageBackgroundSize(e.target.value)}
                margin="dense"
                variant="outlined"
              >
                <MenuItem value="cover">Cover</MenuItem>
                <MenuItem value="contain">Contain</MenuItem>
                <MenuItem value="auto">Auto</MenuItem>
                <MenuItem value="100% 100%">Stretch</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Background Position"
                value={pageBackgroundPosition}
                onChange={(e) => setPageBackgroundPosition(e.target.value)}
                margin="dense"
                variant="outlined"
              >
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="top">Top</MenuItem>
                <MenuItem value="bottom">Bottom</MenuItem>
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="right">Right</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <TextField
            select
            fullWidth
            label="Background Repeat"
            value={pageBackgroundRepeat}
            onChange={(e) => setPageBackgroundRepeat(e.target.value)}
            margin="dense"
            variant="outlined"
          >
            <MenuItem value="no-repeat">No Repeat</MenuItem>
            <MenuItem value="repeat">Repeat</MenuItem>
            <MenuItem value="repeat-x">Repeat X</MenuItem>
            <MenuItem value="repeat-y">Repeat Y</MenuItem>
          </TextField>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Canvas Size
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Canvas Width (px)"
                type="number"
                value={canvasWidth}
                onChange={(e) => setCanvasWidth(Number(e.target.value))}
                margin="dense"
                variant="outlined"
                inputProps={{ min: 800, max: 3840 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Canvas Height (px)"
                type="number"
                value={canvasHeight}
                onChange={(e) => setCanvasHeight(Number(e.target.value))}
                margin="dense"
                variant="outlined"
                inputProps={{ min: 600, max: 2160 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPageSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SimplePageDesigner;